import { ShoppingCart } from 'lucide-react';

export interface BuyAFeatureLiveItem {
  id: string;
  name: string;
  price: number;
  /** What this person has pledged toward the item. */
  pledged: number;
}

interface BuyAFeatureLiveProps {
  features: BuyAFeatureLiveItem[];
  budget: number;
  remainingBudget: number;
  onRaise: (featureId: string) => void;
  onLower: (featureId: string) => void;
}

/**
 * One person's pledges in a live Buy-a-Feature session. People pool money
 * (owner, 2026-10-28): each pledges toward items in $5 steps, up to the item's
 * price, and an item is bought when the room's pledges reach its price — so an
 * item nobody can afford alone can still be bought together.
 */
export function BuyAFeatureLive({
  features,
  budget,
  remainingBudget,
  onRaise,
  onLower,
}: BuyAFeatureLiveProps) {
  const spent = budget - remainingBudget;

  return (
    <div>
      <div className="flex flex-wrap justify-between gap-2 mb-4 font-heading text-[0.55rem] tracking-[0.08em] uppercase text-[var(--text-ter)]">
        <span className="flex items-center gap-1.5">
          <ShoppingCart className="h-3.5 w-3.5" />
          Budget remaining: <span className="text-[var(--mustard)]">${remainingBudget}</span> / ${budget}
        </span>
        <span>${spent} pledged</span>
      </div>

      <div className="space-y-2">
        {features.map(f => {
          const canRaise = f.pledged < f.price && remainingBudget > 0;
          const hasPledge = f.pledged > 0;
          const share = f.price > 0 ? Math.min(100, Math.round((f.pledged / f.price) * 100)) : 0;
          return (
            <div
              key={f.id}
              className={`flex items-center justify-between gap-3 border px-3 py-3 ${
                hasPledge ? 'border-[var(--green)] bg-[var(--green-dim)]' : 'border-[var(--rule-bright)] bg-[var(--surface)]'
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-body text-[0.82rem] font-medium text-[var(--text-sec)]">{f.name}</div>
                <div className="font-heading text-[0.52rem] text-[var(--text-ter)]">
                  Costs ${f.price} · you ${f.pledged}
                </div>
                <div className="mt-1.5 h-1 w-full bg-[var(--rule)]" aria-hidden>
                  <div className="h-1 bg-[var(--green)]" style={{ width: `${share}%` }} />
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {hasPledge && (
                  <button
                    type="button"
                    onClick={() => onLower(f.id)}
                    aria-label={`Pledge less toward ${f.name}`}
                    className="w-7 h-7 rounded border border-[var(--rule-bright)] text-[var(--text-sec)]"
                  >
                    −
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onRaise(f.id)}
                  disabled={!canRaise}
                  aria-label={`Pledge more toward ${f.name}`}
                  className="w-7 h-7 rounded border border-[var(--green)] text-[var(--green)] disabled:opacity-30"
                >
                  +
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <p className="font-heading text-[0.48rem] tracking-[0.1em] uppercase text-[var(--text-dim)] text-center mt-4">
        Pledge $5 at a time toward what matters most — the room buys what its pledges cover together. Submit when done
      </p>
    </div>
  );
}
